import {
  type CommonPowerSyncDatabase,
  type CrudEntry,
  type CrudTransaction,
  type PowerSyncBackendConnector,
  UpdateType,
} from "@powersync/common"
import { enrichSpeciesWithAlaGuid } from "@fieldbook/common/lib/speciesEnrichment"
import { supabase } from "@fieldbook/common/supabase"
import { createFieldbookSupabaseClientForToken } from "@fieldbook/common/supabaseClient"
import type { Database } from "@fieldbook/common/types/database"
import * as Sentry from "@sentry/react"
import posthog from "posthog-js"
import { fetchWithTimeout } from "@/lib/fetchWithTimeout"
import {
  getLiveSessionWithTimeout,
  isAccessTokenAccepted,
} from "@/lib/auth/liveSession"
import type { ErrorClassification } from "@/lib/queryClient/MutationError"
import {
  applyIdRemaps,
  loadIdRemaps,
  PARENT_REFERENCE_COLUMNS,
  type ParentTable,
  recordIdRemap,
} from "./idRemaps"
import {
  getLastObservationDeleteDiagnosticContext,
  recordPowerSyncUploadAfterObservationDelete,
} from "./observationDeleteTelemetry"
import {
  markUploadQueueEmpty,
  markUploadRead,
  withUploadMarkLock,
} from "./unconfirmedUploads"
import {
  QUEUE_HEAD_STALL_THRESHOLD_MS,
  reportRowQueueHeadFailing,
  reportRowQueueMoved,
  reportRowsUploaded,
  reportRowsWaitingForConnection,
} from "./uploadSignals"

type TableName = keyof Database["public"]["Tables"]

type TokenBoundClient = ReturnType<typeof createFieldbookSupabaseClientForToken>

let tokenBoundClient: { accessToken: string; client: TokenBoundClient } | null =
  null

/**
 * Reuse one token-bound client until the session's token changes, rather than
 * building a new client (and its unused realtime/storage sub-clients) for
 * every transaction.
 */
function getTokenBoundClient(accessToken: string): TokenBoundClient {
  if (tokenBoundClient?.accessToken !== accessToken) {
    tokenBoundClient = {
      accessToken,
      client: createFieldbookSupabaseClientForToken(accessToken, {
        fetch: fetchWithTimeout,
      }),
    }
  }
  return tokenBoundClient.client
}

const POWERSYNC_URL = import.meta.env.VITE_POWERSYNC_URL

/**
 * Fields that are stored as JSON text in SQLite but need to be
 * parsed back to objects before sending to Supabase (JSONB columns).
 */
const JSON_FIELDS: Record<string, string[]> = {
  survey_sites: ["custom_fields", "recorders"],
  observations: ["custom_fields", "recorders"],
  species: ["metadata"],
  geospatial_data: ["geojson", "properties"],
  tracks: ["geojson"],
  project: ["metadata", "site_types", "observation_custom_fields"],
}

/**
 * Fields stored as INTEGER (0/1) in SQLite that need to be
 * converted back to booleans before sending to Supabase.
 */
const BOOLEAN_FIELDS: Record<string, string[]> = {
  observations: ["is_collection", "species_indeterminate"],
  determinations: ["species_indeterminate"],
}

/**
 * Upload order for tables: every table comes after all the tables it holds
 * foreign keys to, so parent rows reach the server before their children.
 *
 * This is a strict order rather than a set of priority tiers because a
 * batch sorts operations from many transactions together. Within a single
 * transaction, insertion order used to settle ties between e.g.
 * `collections` and `observations`, but across transactions it can't.
 */
const TABLE_UPLOAD_ORDER = [
  // No foreign keys to other uploaded tables
  "species",
  "survey_sites",
  "collections",
  "photos",
  "geospatial_data",
  "tracks",
  // → survey_sites
  "planned_sites",
  // → species, survey_sites, collections
  "observations",
  // → species, collections
  "determinations",
  // → planned_sites
  "planned_site_assignments",
  // → photos and the parent record
  "survey_site_photos",
  "observation_photos",
  "geospatial_data_photos",
]

function tableUploadRank(table: string): number {
  const rank = TABLE_UPLOAD_ORDER.indexOf(table)
  return rank === -1 ? TABLE_UPLOAD_ORDER.length : rank
}

/**
 * Stop adding transactions to a batch once it holds this many operations.
 * A soft limit: the transaction that crosses it is still included whole.
 */
const MAX_BATCH_OPERATIONS = 200

/** Most rows sent in one bulk upsert request. */
const MAX_ROWS_PER_UPSERT = 100

/**
 * Rough cap on one bulk upsert's JSON body, so a run of large rows (e.g.
 * geospatial geometry) doesn't become one huge request on a slow link. A
 * single row bigger than this is still sent, on its own.
 */
const MAX_UPSERT_BYTES = 512 * 1024

/**
 * Maximum number of times to retry a transaction that fails with a
 * non-transient error (e.g. FK violation, or a server-rejected op) before
 * discarding it.
 */
const MAX_NON_TRANSIENT_RETRIES = 5

/**
 * Base delay (ms) between retries for non-transient errors, to give
 * dependent rows time to sync (or a flaky rejection to clear). The
 * actual delay increases with the attempt number
 * (NON_TRANSIENT_RETRY_DELAY_MS * attempt: 2s, 4s, 6s, 8s, 10s) so a
 * transaction that's genuinely poisoned doesn't hammer the server.
 */
const NON_TRANSIENT_RETRY_DELAY_MS = 2000

/**
 * PostgreSQL error codes that indicate a dependency hasn't been
 * uploaded yet (may resolve on retry after the dependency syncs).
 */
const DEPENDENCY_ERROR_CODES = new Set([
  "23503", // foreign_key_violation
])

/**
 * Not a PostgreSQL code: set by the connector when a PATCH matched zero
 * rows, which PostgREST reports as success (see throwRowNotFound).
 */
const ROW_NOT_FOUND_CODE = "FIELDBOOK_ROW_NOT_FOUND"

/**
 * Not a PostgreSQL code: recorded in sync_failures for a row parked because
 * a parent it references was parked (see findParkedParent). The server's
 * own code is kept in `server_code` and its message in `details`.
 */
const PARENT_FAILED_CODE = "FIELDBOOK_PARENT_FAILED"

/**
 * PostgreSQL error codes that are permanently non-retryable.
 * The operation will never succeed regardless of retries.
 *
 * Note: 42501 (insufficient_privilege) is also raised when the Supabase
 * auth session was missing or stale at request time (e.g. a stalled token
 * refresh), in which case the request was evaluated as the `anon` role or
 * with an expired JWT and rejected by RLS rather than genuinely lacking
 * permission. uploadData checks the session with the auth server before
 * treating a 42501 as permanent.
 */
const PERMANENT_ERROR_CODES = new Set([
  "23514", // check_violation
  "42501", // insufficient_privilege
  ROW_NOT_FOUND_CODE,
])

/**
 * PostgREST/Postgres error codes that mean the *server or connection* had a
 * transient problem, not that this row's data is bad. These are rethrown
 * so PowerSync retries the transaction untouched — they never count
 * against MAX_NON_TRANSIENT_RETRIES and never reach sync_failures.
 */
const TRANSIENT_ERROR_CODES = new Set([
  "PGRST301", // JWT expired
  "PGRST303", // JWT issue
  "40001", // serialization_failure
  "40P01", // deadlock_detected
  "57014", // query_canceled (statement timeout)
  "57P01", // admin_shutdown
  "57P02", // crash_shutdown
  "57P03", // cannot_connect_now
])

/** HTTP statuses that indicate a transient server/session problem. */
const TRANSIENT_HTTP_STATUSES = new Set([401, 408, 429])

/**
 * Parse opData for a table, converting JSON text fields back to objects.
 */
function prepareForSupabase(
  table: string,
  data: Record<string, unknown>,
): Record<string, unknown> {
  const jsonFields = JSON_FIELDS[table]
  const booleanFields = BOOLEAN_FIELDS[table]
  if (!jsonFields && !booleanFields) return data

  const result = { ...data }
  for (const field of jsonFields ?? []) {
    const value = result[field]
    if (typeof value === "string") {
      try {
        result[field] = JSON.parse(value)
      } catch {
        // Leave as-is if not valid JSON
      }
    }
  }

  for (const field of booleanFields ?? []) {
    if (field in result) {
      result[field] = Boolean(result[field])
    }
  }

  return result
}

/**
 * Safely complete a transaction, logging (but not re-throwing) if completion
 * itself fails. A failed `complete()` leaves the CRUD entry in the queue —
 * PowerSync will re-deliver it on the next `uploadData` call, which is
 * acceptable because the data was already saved to `sync_failures`.
 */
async function safeComplete(transaction: CrudTransaction): Promise<void> {
  try {
    await transaction.complete()
  } catch (completeError) {
    console.error(
      "[PowerSync] transaction.complete() failed — CRUD entry remains in queue:",
      completeError,
    )
    Sentry.captureException(
      completeError instanceof Error
        ? completeError
        : new Error(
            `transaction.complete() failed: ${errorMessage(completeError)}`,
          ),
    )
  }
}

/**
 * Result of a species upsert that triggered a remap.
 * When a species with the same name already exists on the server,
 * local references are remapped from localId → serverId.
 * The caller must also patch any remaining in-flight ops in the
 * current transaction that still reference localId.
 */
type SpeciesRemapResult = {
  remapped: true
  localId: string
  serverId: string
} | null

/**
 * Handle species upsert with duplicate name resolution.
 *
 * Species have a UNIQUE constraint on (organisation_id, name). When syncing
 * a locally-created species, one with the same name may already exist in
 * Supabase (created by another device or via the web app).
 *
 * When this happens, we look up the canonical server species and remap all
 * local references (observations, determinations) from the local ID to the
 * server ID. This prevents FK violations when those rows are uploaded.
 *
 * Returns remap info so the caller can patch in-flight ops in the same
 * transaction that still reference the old localId.
 */
async function upsertSpecies(
  client: TokenBoundClient,
  localId: string,
  data: Record<string, unknown>,
  database: CommonPowerSyncDatabase,
): Promise<SpeciesRemapResult> {
  const { error, status } = await client
    .from("species")
    .upsert({ id: localId, ...data } as never)

  if (!error) return null

  // PostgreSQL 23505 = unique_violation (organisation_id, name)
  if (error.code !== "23505") {
    throwIfError({ error, status })
  }

  console.info(
    `[PowerSync] Species "${data.name}" already exists in org, remapping local references`,
  )

  // The 23505 is guaranteed to be a conflict on (organisation_id, name) —
  // the only unique constraint on this table — so a name lookup always
  // finds the exact row that caused it, regardless of which side (if
  // either) has an ala_guid.
  const {
    data: existing,
    error: lookupError,
    status: lookupStatus,
  } = await client
    .from("species")
    .select("id, ala_guid")
    .eq("organisation_id", data.organisation_id as string)
    .eq("name", data.name as string)
    .limit(1)
    .maybeSingle()

  if (lookupError) {
    console.error(
      `[PowerSync] Species lookup failed for "${data.name}":`,
      lookupError,
    )
    throwIfError({ error: lookupError, status: lookupStatus })
  }

  if (!existing) {
    // The server reported a name conflict but we can't see the row (RLS,
    // or it was deleted in between). Carrying on would upload dependent
    // rows pointing at a species the server doesn't have, so surface the
    // original conflict instead of a misleading FK violation further on.
    console.warn(
      `[PowerSync] Could not find existing species for "${data.name}" after a duplicate-name conflict`,
    )
    throwIfError({ error, status })
    return null // unreachable: error is non-null, so throwIfError threw
  }

  const serverId = existing.id

  // This upload may carry an ala_guid that the canonical row doesn't have
  // yet (e.g. the canonical row came from an offline "custom species" entry
  // on another device). Attach it now rather than silently discarding it
  // when the local duplicate is deleted below. A failure here propagates
  // (SpeciesEnrichmentError carries code/status for classification) so the
  // transaction is retried before the local row — the only other copy of
  // this ala_guid — is deleted. Retrying is safe: the upsert above wrote
  // nothing, and the enrichment is guarded against overwriting.
  if (data.ala_guid && !existing.ala_guid) {
    await enrichSpeciesWithAlaGuid(client, {
      speciesId: serverId,
      alaGuid: data.ala_guid as string,
    })
  }

  if (serverId === localId) {
    // Same ID — nothing to remap
    return null
  }

  // Remap all local references from localId → serverId.
  // These updates generate new CRUD entries (PATCH) with the correct FK.
  console.info(`[PowerSync] Remapping species ${localId} → ${serverId}`)

  await database.writeTransaction(async (tx) => {
    await tx.execute(
      "UPDATE observations SET species_id = ? WHERE species_id = ?",
      [serverId, localId],
    )
    await tx.execute(
      "UPDATE determinations SET species_id = ? WHERE species_id = ?",
      [serverId, localId],
    )
    // Delete the local species row — the server version will sync down
    await tx.execute("DELETE FROM species WHERE id = ?", [localId])
    // Rows parked behind this species were queued with the local id, and can
    // be restored after this transaction commits. Record the mapping so they
    // follow it instead of referencing a species the server will never have.
    await recordIdRemap(tx, {
      table: "species",
      localId,
      serverId,
    })
  })

  return { remapped: true, localId, serverId }
}

type UpsertRow = Record<string, unknown> & { id: string }

/**
 * A run of consecutive PUTs waiting to go up as bulk upserts: one table,
 * one exact set of keys, and no row twice.
 *
 * The key set has to match because a bulk upsert sends the union of every
 * row's keys and fills the missing ones with null, which would overwrite
 * server-set columns that a single-row upsert leaves alone. Rows are never
 * regrouped across a run boundary: order between different rows of one
 * table can matter too (e.g. the partial unique index allowing one
 * collection observation per collection_id), so requests go up in exactly
 * the queue order of the rows they carry.
 */
type PendingUpserts = {
  table: TableName
  keySet: string
  rows: UpsertRow[]
  ids: Set<string>
}

/**
 * Split rows into request-sized chunks, by row count and by approximate
 * JSON size.
 */
function chunkRows(rows: UpsertRow[]): UpsertRow[][] {
  const chunks: UpsertRow[][] = []
  let chunk: UpsertRow[] = []
  let chunkBytes = 0
  for (const row of rows) {
    const rowBytes = JSON.stringify(row).length
    if (
      chunk.length > 0 &&
      (chunk.length >= MAX_ROWS_PER_UPSERT ||
        chunkBytes + rowBytes > MAX_UPSERT_BYTES)
    ) {
      chunks.push(chunk)
      chunk = []
      chunkBytes = 0
    }
    chunk.push(row)
    chunkBytes += rowBytes
  }
  if (chunk.length > 0) chunks.push(chunk)
  return chunks
}

async function flushUpserts(
  client: TokenBoundClient,
  pending: PendingUpserts,
): Promise<void> {
  for (const chunk of chunkRows(pending.rows)) {
    // A lone row goes up as an object, exactly as a per-row upsert would.
    const { error, status } = await client
      .from(pending.table)
      .upsert((chunk.length === 1 ? chunk[0] : chunk) as never)
    throwIfError({ error, status })
  }
}

/**
 * Send a list of CRUD operations to Supabase in table dependency order,
 * with consecutive PUTs to the same table combined into bulk upserts.
 *
 * Operations for the same table keep their exact queue order: a run of
 * PUTs is only extended by the next PUT for the same table, with the same
 * keys, for a row not already in the run. Anything else sends the run
 * first. PATCH and DELETE go
 * up one row at a time. Species PUTs also go up one at a time, because a
 * duplicate name has to be resolved (and remapped) per row.
 *
 * Throws on the first failed request. Everything sent before it has
 * already been applied; every request here is idempotent, so re-sending
 * the same operations later is safe.
 */
async function uploadOperations(
  client: TokenBoundClient,
  database: CommonPowerSyncDatabase,
  operations: CrudEntry[],
): Promise<void> {
  const sortedOps = [...operations].sort(
    (a, b) => tableUploadRank(a.table) - tableUploadRank(b.table),
  )

  // Species deduplicated earlier on this device: rows queued before the
  // remap (e.g. a child restored from sync_failures after its parent's
  // transaction committed) still carry the local id, which the server no
  // longer has. Resolve it from the recorded mapping.
  const remaps = await loadIdRemaps(database)
  // Species deduplicated earlier in this upload transaction: the ops later
  // in the same transaction still carry the local id (the recorded remap
  // above is only read once, and this one isn't written to it yet).
  const speciesIdRemaps = new Map<string, string>()

  const prepare = (op: CrudEntry) => {
    const data = prepareForSupabase(
      op.table,
      applyIdRemaps(op.opData ?? {}, remaps),
    )
    const speciesId = data.species_id
    if (typeof speciesId !== "string") return data
    const remapped = speciesIdRemaps.get(speciesId)
    return remapped ? { ...data, species_id: remapped } : data
  }

  let pending: PendingUpserts | null = null
  const flush = async () => {
    if (!pending) return
    const toSend: PendingUpserts = pending
    pending = null
    await flushUpserts(client, toSend)
  }

  for (const op of sortedOps) {
    const table = op.table as TableName
    const { id } = op

    if (op.op === UpdateType.PUT && table !== "species") {
      const row: UpsertRow = { id, ...prepare(op) }
      const keySet = Object.keys(row).sort().join(",")
      if (
        pending &&
        (pending.table !== table ||
          pending.keySet !== keySet ||
          pending.ids.has(id))
      ) {
        await flush()
      }
      pending ??= { table, keySet, rows: [], ids: new Set() }
      pending.rows.push(row)
      pending.ids.add(id)
      continue
    }

    await flush()

    switch (op.op) {
      case UpdateType.PUT: {
        const remap = await upsertSpecies(client, id, prepare(op), database)
        if (remap) {
          speciesIdRemaps.set(remap.localId, remap.serverId)
        }
        break
      }
      case UpdateType.PATCH: {
        const changes = prepare(op)
        // An UPDATE that changed nothing queues a PATCH with no columns.
        // There is nothing to send, and PostgREST would answer an empty
        // update with zero rows, which would park it as not found.
        if (Object.keys(changes).length === 0) break
        // .select("id") makes PostgREST return the updated rows, which also
        // requires the updated row to pass the table's SELECT policy. Every
        // synced table's UPDATE policy is narrower than its SELECT policy
        // (both scope to the user's org), so a row the user may update is
        // one they may see, and a visible-but-not-updatable row comes back
        // as zero rows rather than an error.
        const { data, error, status } = await client
          .from(table)
          .update(changes as never)
          .eq("id" as never, id)
          .select("id")
        throwIfError({ error, status })
        // PostgREST reports no error when an UPDATE matches zero rows, either
        // because RLS filtered the row out or because it isn't on the server
        // (e.g. its create was parked). Completing would drop the edit, and
        // the next sync would quietly revert it.
        if (!data?.length) throwRowNotFound(table, id, status)
        break
      }
      case UpdateType.DELETE: {
        const { data, error, status } = await client
          .from(table)
          .delete()
          .eq("id" as never, id)
          .select("id")
        throwIfError({ error, status })
        // Zero rows usually means the row is already gone, which is the
        // outcome a delete wants, so it still completes. Not covered: RLS
        // refusing a delete the user isn't allowed to make (e.g. someone
        // else's record under a creator-or-admin policy) also matches zero
        // rows, and the next sync then restores the row locally. Telling the
        // two apart would need a follow-up SELECT of the row.
        if (!data?.length) {
          Sentry.addBreadcrumb({
            category: "powersync_upload",
            level: "info",
            message: `DELETE matched no rows on ${table}`,
            data: { table, id, status },
          })
        }
        break
      }
    }
  }

  await flush()
}

/**
 * Classify a PostgreSQL/PostgREST error code into an ErrorClassification.
 */
function classifyPgCode(code: string | undefined): ErrorClassification {
  if (!code) return "internal"
  if (code === ROW_NOT_FOUND_CODE) return "validation"
  if (code.startsWith("22")) return "validation" // data_exception (e.g. 22P02)
  if (code.startsWith("23")) return "validation"
  if (code.startsWith("42")) return "validation"
  if (code.startsWith("PGRST2")) return "validation" // schema-cache mismatch, e.g. PGRST204
  return "internal"
}

/**
 * Extract a string field from an unknown error object, if present.
 */
function errorField(error: unknown, field: string): string | null {
  if (error && typeof error === "object" && field in error) {
    const value = (error as Record<string, unknown>)[field]
    return typeof value === "string" ? value : null
  }
  return null
}

/**
 * Extract a human-readable message from an error.
 * Handles Error instances, Supabase PostgrestError objects (which have
 * a `message` property but don't extend Error), and unknown values.
 */
function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  const msg = errorField(error, "message")
  if (msg) return msg
  return String(error)
}

/**
 * Extract the HTTP status attached by throwIfError, if present. Absent
 * entirely means the error wasn't a classified PostgREST response at
 * all — it's a thrown exception (fetch rejecting outright, or a local
 * SQLite error from database.writeTransaction).
 */
function errorStatus(error: unknown): number | undefined {
  if (error && typeof error === "object" && "status" in error) {
    const value = (error as Record<string, unknown>).status
    return typeof value === "number" ? value : undefined
  }
  return undefined
}

/**
 * Minimal shape of the `error` half of a postgrest-js response. Not an
 * Error instance — postgrest-js JSON.parses the response body (or, for a
 * codeless failure like an HTML gateway error page, wraps it as
 * `{ message: body }`) into a plain object.
 */
type PostgrestErrorLike = {
  message: string
  code?: string | null
  details?: string | null
  hint?: string | null
}

/**
 * postgrest-js resolves every query-builder call with `{ error, status }`
 * rather than rejecting — `error` itself never carries the HTTP status
 * that came with it. A fetch/network/abort failure resolves with
 * `status: 0`; a gateway 502/504 with an HTML body resolves with a
 * codeless `error.message` and that status. uploadData's catch block
 * needs both together to tell a transient failure from a genuine
 * rejection, so every call site throws through this helper instead of
 * `if (error) throw error`.
 *
 * No-op when there was no error.
 */
function throwIfError({
  error,
  status,
}: {
  error: PostgrestErrorLike | null
  status: number
}): void {
  if (!error) return
  throw {
    message: error.message,
    code: error.code ?? undefined,
    details: error.details ?? undefined,
    hint: error.hint ?? undefined,
    status,
  }
}

/**
 * Throw, in throwIfError's shape, for a PATCH that the server accepted but
 * that matched zero rows. It counts as a permanent rejection, so the edit is
 * parked in sync_failures rather than completed and lost.
 */
function throwRowNotFound(table: string, id: string, status: number): never {
  throw {
    message: "Record not found or not editable",
    code: ROW_NOT_FOUND_CODE,
    details: `The update to ${table} ${id} matched no rows on the server: the record doesn't exist there, or you don't have permission to edit it.`,
    hint: undefined,
    status,
  }
}

/**
 * Why an upload failure was treated as transient. Logged and tagged on the
 * stall signal so the catch-all "exception" case — a thrown error that
 * never reached PostgREST, which could equally be a local SQLite failure
 * or a bug in this loop — stays distinguishable from genuine network and
 * server trouble.
 */
type TransientErrorKind =
  | "exception"
  | "network"
  | "http_status"
  | "pg_code"
  | "unconfirmed_token"

/**
 * Classify an upload failure as transient (returning why), or null if it isn't: one that should be rethrown so
 * PowerSync retries it untouched by the non-transient retry budget (never
 * counts toward MAX_NON_TRANSIENT_RETRIES, never reaches sync_failures).
 * Covers anything that says "the server/network/session had a problem,"
 * as opposed to "this row will never be accepted":
 *
 * - No `status` field at all — a thrown exception rather than a
 *   classified PostgREST response (fetch rejecting, a local SQLite
 *   error). Says nothing about the row's validity, so treat as transient.
 * - `status === 0` — postgrest-js's shape for a fetch/network/abort
 *   failure.
 * - 5xx, 408 (timeout), 429 (rate limited), 401 (a token that expired
 *   between fetchCredentials and the request landing).
 * - PGRST301/PGRST303 (JWT-related) and Postgres codes for transient
 *   server-side conditions: serialization/deadlock conflicts, statement
 *   timeout, admin/connection shutdown, connection-class (08xxx) and
 *   resource-exhaustion (53xxx) errors.
 */
function transientErrorKind(error: unknown): TransientErrorKind | null {
  const status = errorStatus(error)
  if (status === undefined) return "exception"
  if (status === 0) return "network"
  if (status >= 500 || TRANSIENT_HTTP_STATUSES.has(status)) {
    return "http_status"
  }

  const code = errorField(error, "code")
  if (code) {
    if (TRANSIENT_ERROR_CODES.has(code)) return "pg_code"
    if (code.startsWith("08") || code.startsWith("53")) return "pg_code"
  }

  return null
}

function appVersion(): string {
  return typeof __BUILD_ID__ === "undefined" ? "unknown" : __BUILD_ID__
}

function transactionOperationSummary(
  transaction: CrudTransaction,
): Record<string, number> {
  const summary: Record<string, number> = {}
  for (const operation of transaction.crud) {
    const key = `${operation.table}:${operation.op}`
    summary[key] = (summary[key] ?? 0) + 1
  }
  return summary
}

function hasObservationDelete(transaction: CrudTransaction): boolean {
  return transaction.crud.some(
    (operation) =>
      operation.table === "observations" && operation.op === UpdateType.DELETE,
  )
}

/**
 * Whether a transaction can share a batch with its neighbours.
 *
 * A batch sorts operations from all its transactions by table, which moves
 * some of them ahead of operations queued before them. That is safe for
 * inserts and updates, because the sort puts parents first. It is not safe
 * for deletes (a delete moved ahead of an earlier insert that references
 * the deleted row makes that insert fail), or for tables outside
 * TABLE_UPLOAD_ORDER, whose dependencies the sort knows nothing about. Such
 * transactions still upload, just on their own.
 */
function isBatchable(transaction: CrudTransaction): boolean {
  return transaction.crud.every(
    (operation) =>
      operation.op !== UpdateType.DELETE &&
      TABLE_UPLOAD_ORDER.includes(operation.table),
  )
}

function lastClientId(transaction: CrudTransaction): number | undefined {
  return transaction.crud[transaction.crud.length - 1]?.clientId
}

/**
 * A rejected batch of this many transactions or fewer isn't halved again:
 * its transactions upload one at a time instead.
 */
const MAX_ONE_BY_ONE_BATCH = 3

/**
 * How many rejected batches may be narrowed down to one-at-a-time uploads
 * within one rejection window before the rest of the window goes one at a
 * time outright. Each narrowing costs about log2(batch size) rejected
 * requests, so when most rows are bad (e.g. a whole table rejected after a
 * schema change) halving would cost far more than it saves.
 */
const MAX_NARROWED_REJECTIONS = 3

/**
 * State for finding rejected rows after a batch is rejected, by halving.
 *
 * A batch error can't say which row was bad, so the rejected batch's first
 * half is retried at once, and whichever part is still rejected is halved
 * again. Halves that succeed are prefixes of the queue, so they complete
 * normally. Once a rejected batch is down to MAX_ONE_BY_ONE_BATCH
 * transactions, those upload one at a time, and the per-transaction path
 * applies the usual retry and park policy to whichever one is bad. Then
 * batches go back to full size.
 *
 * The window lasts until the queue head moves past the first rejected
 * batch. Kept in memory only: if the app restarts first, the next batch
 * simply fails again and starts a new window.
 */
type RejectionWindow = {
  /** Client id of the last operation in the batch first rejected. */
  throughClientId: number
  /** Most transactions a batch may hold while in the window. */
  maxTransactions: number
  /** Upload one at a time until the queue head moves past this client id. */
  oneByOneThroughClientId: number | null
  /** Rejected batches narrowed down to one-at-a-time uploads so far. */
  narrowedRejections: number
}

let rejectionWindow: RejectionWindow | null = null

/**
 * After `batch` was rejected, record it in the rejection window and return
 * the transactions to try next: the batch's first half, or just its first
 * transaction once the batch is small enough to go one at a time.
 */
function narrowAfterRejection(batch: CrudTransaction[]): CrudTransaction[] {
  const batchLastClientId = lastClientId(batch[batch.length - 1]) ?? -1
  rejectionWindow ??= {
    throughClientId: batchLastClientId,
    maxTransactions: batch.length,
    oneByOneThroughClientId: null,
    narrowedRejections: 0,
  }

  if (batch.length <= MAX_ONE_BY_ONE_BATCH) {
    rejectionWindow.oneByOneThroughClientId = batchLastClientId
    rejectionWindow.narrowedRejections += 1
    return [batch[0]]
  }

  const half = Math.floor(batch.length / 2)
  rejectionWindow.maxTransactions = half
  return batch.slice(0, half)
}

/**
 * The most transactions the batch starting at `head` may hold, given any
 * rejection window in progress.
 */
function maxBatchTransactions(head: CrudTransaction): number {
  if (!rejectionWindow) return Number.POSITIVE_INFINITY

  const headClientId = lastClientId(head)
  if (
    headClientId === undefined ||
    headClientId > rejectionWindow.throughClientId
  ) {
    rejectionWindow = null
    return Number.POSITIVE_INFINITY
  }

  const oneByOneThrough = rejectionWindow.oneByOneThroughClientId
  if (oneByOneThrough !== null) {
    if (headClientId <= oneByOneThrough) return 1
    // Past the narrowed-down transactions: back to full batches, unless
    // this window has already had to narrow down too many rejections.
    rejectionWindow.oneByOneThroughClientId = null
    rejectionWindow.maxTransactions =
      rejectionWindow.narrowedRejections >= MAX_NARROWED_REJECTIONS
        ? 1
        : Number.POSITIVE_INFINITY
  }
  return rejectionWindow.maxTransactions
}

/**
 * Read the next queued transaction, and mark it as read for upload if
 * `willUpload` says it'll be sent. From then on its rows may reach the
 * server while still in ps_crud, which deleteObservationWithDatabase needs
 * to know (see unconfirmedUploads.ts). The read and the mark happen
 * together, under the lock a delete takes for its write transaction.
 */
async function readForUpload(
  iterator: AsyncIterator<CrudTransaction, null>,
  willUpload: (transaction: CrudTransaction) => boolean = () => true,
): Promise<CrudTransaction | null> {
  return withUploadMarkLock(async () => {
    const next = (await iterator.next()).value
    if (next && willUpload(next)) markUploadRead(next)
    return next ?? null
  })
}

/**
 * The transactions to upload together in this uploadData call, starting
 * with the queue head: consecutive batchable transactions up to
 * MAX_BATCH_OPERATIONS operations (and any limit set while narrowing down
 * a rejected batch), or just the head if it can't be batched.
 */
async function collectBatch(
  head: CrudTransaction,
  iterator: AsyncIterator<CrudTransaction, null>,
): Promise<CrudTransaction[]> {
  const maxTransactions = maxBatchTransactions(head)
  if (maxTransactions <= 1 || !isBatchable(head)) return [head]

  const batch = [head]
  let operationCount = head.crud.length
  while (
    operationCount < MAX_BATCH_OPERATIONS &&
    batch.length < maxTransactions
  ) {
    const next = await readForUpload(iterator, isBatchable)
    if (!next || !isBatchable(next)) break
    batch.push(next)
    operationCount += next.crud.length
  }
  return batch
}

function batchOperationSummary(
  batch: CrudTransaction[],
): Record<string, number> {
  const summary: Record<string, number> = {}
  for (const transaction of batch) {
    for (const [key, count] of Object.entries(
      transactionOperationSummary(transaction),
    )) {
      summary[key] = (summary[key] ?? 0) + count
    }
  }
  return summary
}

type FailurePolicy = "permanent" | "dependency_exhausted" | "rejected_exhausted"

function captureUploadFailure(
  error: unknown,
  transaction: CrudTransaction,
  failurePolicy: FailurePolicy,
  retryCount?: number,
): void {
  const pgCode = errorField(error, "code")
  try {
    Sentry.captureException(
      error instanceof Error ? error : new Error(errorMessage(error)),
      {
        tags: {
          component: "powersync_upload",
          app_version: appVersion(),
          pg_code: pgCode ?? "unknown",
          failure_policy: failurePolicy,
        },
        contexts: {
          powersync_upload: {
            operation_count: transaction.crud.length,
            operation_summary: transactionOperationSummary(transaction),
            has_observation_delete: hasObservationDelete(transaction),
            retry_count: retryCount ?? 0,
          },
          last_observation_delete: getLastObservationDeleteDiagnosticContext(),
        },
      },
    )
  } catch {
    // Telemetry must never interfere with queue recovery.
  }
}

function captureObservationDeleteUploaded(transaction: CrudTransaction): void {
  if (!hasObservationDelete(transaction)) return
  try {
    posthog.capture("powersync_observation_delete_uploaded", {
      app_version: appVersion(),
      operation_count: transaction.crud.length,
      operation_summary: transactionOperationSummary(transaction),
    })
  } catch {
    // Telemetry must never interfere with queue processing.
  }
}

/**
 * Save all operations from a failed transaction into the local-only
 * `sync_failures` table before discarding the transaction.
 *
 * This preserves the user's data so it can be surfaced in the UI for
 * retry or editing, preventing silent data loss when the server
 * rejects a CRUD operation.
 */
let lastFailedAtMs = 0

/**
 * A `failed_at` for a newly parked transaction. It also identifies the
 * transaction's group (see retrySyncFailure), so two transactions parked in
 * the same millisecond, which happens when a parent's children are parked
 * one after another, must not share one.
 */
function nextFailedAt(): string {
  lastFailedAtMs = Math.max(Date.now(), lastFailedAtMs + 1)
  return new Date(lastFailedAtMs).toISOString()
}

async function saveFailedTransaction(
  database: CommonPowerSyncDatabase,
  transaction: CrudTransaction,
  error: unknown,
  parkedParent?: ParkedParent,
): Promise<void> {
  const pgCode = errorField(error, "code")

  const errorInfo = JSON.stringify(
    parkedParent
      ? {
          // Shown to the user, so no table names or ids here.
          message: `Not uploaded because its ${PARENT_LABELS[parkedParent.table]} failed to upload`,
          code: PARENT_FAILED_CODE,
          hint: errorField(error, "hint"),
          // The server's own error, kept for diagnosis.
          details: errorMessage(error),
          status: errorStatus(error) ?? null,
          server_code: pgCode,
          // Lets retrying or dismissing the parent find this row (see
          // syncFailures.ts).
          parent: { table: parkedParent.table, id: parkedParent.id },
        }
      : {
          message: errorMessage(error),
          code: pgCode,
          hint: errorField(error, "hint"),
          details: errorField(error, "details"),
          status: errorStatus(error) ?? null,
        },
  )

  // A row parked because of its parent takes the parent's classification,
  // so they show alike.
  const classification =
    parkedParent?.classification ?? classifyPgCode(pgCode ?? undefined)
  const failedAt = nextFailedAt()

  for (const op of transaction.crud) {
    const id = crypto.randomUUID()
    await database.execute(
      `INSERT INTO sync_failures (id, target_table, entity_id, op_type, op_data, error_info, failed_at, classification, previous_values)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id,
        op.table,
        op.id,
        op.op,
        JSON.stringify(op.opData ?? {}),
        errorInfo,
        failedAt,
        classification,
        // The trackPrevious columns (project_id, a photo link's parent id):
        // all a rejected PATCH or DELETE has to say which project it was in.
        op.previousValues ? JSON.stringify(op.previousValues) : null,
      ],
    )
  }

  console.warn(
    `[PowerSync] Saved ${transaction.crud.length} failed operation(s) to sync_failures`,
  )
}

/** What to call each kind of parent record in a message to the user. */
const PARENT_LABELS: Record<ParentTable, string> = {
  observations: "observation",
  survey_sites: "site",
  geospatial_data: "geospatial record",
  collections: "collection",
  species: "species",
  photos: "photo",
  planned_sites: "planned site",
}

/** A parent record whose create was parked in sync_failures. */
type ParkedParent = { table: ParentTable; id: string; classification: string }

function isParentTable(table: string): table is ParentTable {
  return table in PARENT_LABELS
}

/**
 * Whether the server rejected the row because a record it references isn't
 * there: a foreign key violation, or the set_*_project_id triggers on photo
 * links and determinations failing to find their parent. Only these are
 * explained by a parked parent; a row rejected for its own reasons keeps the
 * usual retries even if its parent is parked.
 */
function isMissingParentError(error: unknown): boolean {
  const code = errorField(error, "code")
  if (code === "23503") return true
  return (
    code === "P0001" &&
    errorMessage(error).startsWith("Could not determine project_id")
  )
}

/**
 * The first parent referenced by `transaction` whose create (PUT) is parked
 * in sync_failures, or null if there is none.
 *
 * A parked create never reached the server, so a row that references it
 * can't upload either: retrying it only holds up the queue. Parents created
 * in the same transaction don't count, since a failure there is the
 * transaction's own. A row with two parked parents is attributed to the
 * earlier one only; retrying that one re-parks the row under the other.
 *
 * Returns null if the lookup itself fails, leaving the usual retry policy to
 * handle the row.
 */
async function findParkedParent(
  database: CommonPowerSyncDatabase,
  transaction: CrudTransaction,
): Promise<ParkedParent | null> {
  const parents = new Map<string, { table: ParentTable; id: string }>()
  for (const op of transaction.crud) {
    for (const [column, table] of Object.entries(PARENT_REFERENCE_COLUMNS)) {
      const id = op.opData?.[column]
      if (typeof id === "string" && id) {
        parents.set(`${table}:${id}`, { table, id })
      }
    }
  }
  for (const op of transaction.crud) parents.delete(`${op.table}:${op.id}`)
  if (parents.size === 0) return null

  const candidates = [...parents.values()]
  try {
    const row = await database.getOptional<{
      target_table: string
      entity_id: string
      classification: string
    }>(
      `SELECT target_table, entity_id, classification FROM sync_failures
       WHERE op_type = ? AND (${candidates
         .map(() => "(target_table = ? AND entity_id = ?)")
         .join(" OR ")})
       ORDER BY failed_at
       LIMIT 1`,
      [UpdateType.PUT, ...candidates.flatMap(({ table, id }) => [table, id])],
    )
    return row && isParentTable(row.target_table)
      ? {
          table: row.target_table,
          id: row.entity_id,
          classification: row.classification,
        }
      : null
  } catch (lookupError) {
    console.warn(
      "[PowerSync] Could not check sync_failures for a parked parent:",
      lookupError,
    )
    return null
  }
}

/**
 * Park a transaction that failed because a parent it references was itself
 * parked: straight away, with no retries, attributed to that parent.
 */
async function parkWithParkedParent({
  database,
  transaction,
  error,
  key,
  parent,
}: {
  database: CommonPowerSyncDatabase
  transaction: CrudTransaction
  error: unknown
  key: string
  parent: ParkedParent
}): Promise<void> {
  console.warn(
    `[PowerSync] ${key} references ${parent.table}:${parent.id}, which failed to upload; saving to sync_failures without retrying:`,
    error,
  )
  try {
    Sentry.addBreadcrumb({
      category: "powersync_upload",
      level: "warning",
      message: `Parked ${key}: parent ${parent.table} failed to upload`,
      data: { key, parent_table: parent.table, parent_id: parent.id },
    })
  } catch {
    // Telemetry must never interfere with queue recovery.
  }
  await saveFailedTransaction(database, transaction, error, parent)
  clearRetryCount(key)
  clearQueueHeadStall()
  await safeComplete(transaction)
}

/**
 * Identify a transaction for retry tracking.
 * Uses the first CRUD operation's table + id as a stable key.
 */
function transactionKey(transaction: CrudTransaction): string {
  const first = transaction.crud[0]
  return first ? `${first.table}:${first.id}` : "unknown"
}

/**
 * Retry a transaction that failed with a non-permanent, non-transient
 * error (an FK dependency, or an outright server rejection), tracking
 * attempts via localStorage with a delay that increases per attempt
 * (NON_TRANSIENT_RETRY_DELAY_MS * count), until MAX_NON_TRANSIENT_RETRIES
 * is exceeded — at which point it's saved to sync_failures and discarded
 * like a permanent error.
 */
async function retryWithBackoffOrDiscard({
  database,
  transaction,
  error,
  key,
  failurePolicy,
  logLabel,
}: {
  database: CommonPowerSyncDatabase
  transaction: CrudTransaction
  error: unknown
  key: string
  failurePolicy: "dependency_exhausted" | "rejected_exhausted"
  logLabel: string
}): Promise<void> {
  const count = getRetryCount(key) + 1
  setRetryCount(key, count)

  if (count > MAX_NON_TRANSIENT_RETRIES) {
    console.error(
      `[PowerSync] ${logLabel} for ${key} after ${count - 1} retries, saving to sync_failures:`,
      error,
    )
    captureUploadFailure(error, transaction, failurePolicy, count)
    await saveFailedTransaction(database, transaction, error)
    clearRetryCount(key)
    clearQueueHeadStall()
    await safeComplete(transaction)
    return
  }

  console.warn(
    `[PowerSync] ${logLabel} for ${key} (attempt ${count}/${MAX_NON_TRANSIENT_RETRIES}), retrying after delay:`,
    error,
  )
  await new Promise((resolve) =>
    setTimeout(resolve, NON_TRANSIENT_RETRY_DELAY_MS * count),
  )
  // Don't throw — return without completing so the transaction stays
  // in the queue and is retried on the next uploadData call.
}

/**
 * Tracks retry counts for transactions that fail with non-transient errors,
 * keyed by a stable identifier derived from the transaction's operations.
 *
 * Persisted to localStorage rather than kept purely in memory: PowerSync's
 * CRUD queue is strictly FIFO (getNextCrudTransaction always returns the
 * oldest uncompleted transaction), so a transaction that never completes
 * blocks every later transaction — and blocks download of anything that
 * touches the same locally-modified rows — indefinitely. An in-memory
 * counter resets on every page reload, and mobile PWAs get backgrounded/
 * reloaded constantly, so a genuinely broken op (e.g. a dangling FK) could
 * otherwise retry forever without ever reaching MAX_NON_TRANSIENT_RETRIES
 * and wedge the whole sync queue.
 */
const RETRY_COUNT_STORAGE_PREFIX = "powersync_retry_count:"

function getRetryCount(key: string): number {
  try {
    const raw = localStorage.getItem(RETRY_COUNT_STORAGE_PREFIX + key)
    if (!raw) return 0
    const parsed = Number.parseInt(raw, 10)
    return Number.isNaN(parsed) ? 0 : parsed
  } catch {
    return 0
  }
}

function setRetryCount(key: string, count: number): void {
  try {
    localStorage.setItem(RETRY_COUNT_STORAGE_PREFIX + key, String(count))
  } catch {
    // Ignore storage errors (e.g. private browsing quota) — worst case
    // retries aren't tracked durably across reloads for this attempt.
  }
}

function clearRetryCount(key: string): void {
  try {
    localStorage.removeItem(RETRY_COUNT_STORAGE_PREFIX + key)
  } catch {
    // Ignore
  }
}

/**
 * Tracks how long the current CRUD queue head has been failing with an
 * error we deliberately never park (rethrown as transient, including the
 * 42501-with-unconfirmed-token path). Those errors are the right call
 * individually — retrying a network blip or a mid-flight JWT expiry
 * shouldn't burn the non-transient retry budget — but if the *same* head
 * keeps failing that way for a long time, the queue is wedged just as
 * surely as if it had hit a poison error, and nothing else in this file
 * would ever surface it. This is a telemetry-only signal: it never
 * changes control flow.
 */
const QUEUE_HEAD_STALL_STORAGE_KEY = "powersync_queue_head_stall"

type QueueHeadStallRecord = {
  key: string
  since: number
  reported: boolean
}

function isQueueHeadStallRecord(value: unknown): value is QueueHeadStallRecord {
  return (
    Boolean(value) &&
    typeof value === "object" &&
    typeof (value as Record<string, unknown>).key === "string" &&
    typeof (value as Record<string, unknown>).since === "number" &&
    typeof (value as Record<string, unknown>).reported === "boolean"
  )
}

function getQueueHeadStallRecord(): QueueHeadStallRecord | null {
  try {
    const raw = localStorage.getItem(QUEUE_HEAD_STALL_STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return isQueueHeadStallRecord(parsed) ? parsed : null
  } catch {
    return null
  }
}

function setQueueHeadStallRecord(record: QueueHeadStallRecord): void {
  try {
    localStorage.setItem(QUEUE_HEAD_STALL_STORAGE_KEY, JSON.stringify(record))
  } catch {
    // Ignore storage errors — worst case the stall signal doesn't fire.
  }
}

/**
 * Clear the stall record. Called wherever clearRetryCount is called on a
 * transaction completing successfully or being parked in sync_failures —
 * at that point whatever the record was tracking is no longer the
 * (still-failing) queue head.
 */
function clearQueueHeadStall(): void {
  reportRowQueueMoved()
  try {
    localStorage.removeItem(QUEUE_HEAD_STALL_STORAGE_KEY)
  } catch {
    // Ignore
  }
}

/**
 * Record a transient-error rethrow against the queue-head stall tracker,
 * and report to Sentry (once, level "warning") the first time the same
 * head is seen still failing more than QUEUE_HEAD_STALL_THRESHOLD_MS
 * after it was first observed failing.
 *
 * Wrapped in try/catch — like captureUploadFailure, telemetry must never
 * interfere with queue recovery.
 */
function recordQueueHeadStall(
  key: string,
  error: unknown,
  transaction: CrudTransaction,
  transientKind: TransientErrorKind,
): void {
  try {
    const existing = getQueueHeadStallRecord()
    const now = Date.now()

    if (!existing || existing.key !== key) {
      setQueueHeadStallRecord({ key, since: now, reported: false })
      return
    }

    if (existing.reported) return

    const stalledMs = now - existing.since
    if (stalledMs <= QUEUE_HEAD_STALL_THRESHOLD_MS) return

    const pgCode = errorField(error, "code")
    const httpStatus = errorStatus(error)

    Sentry.captureMessage(
      `[PowerSync] Queue head stalled for ${Math.round(stalledMs / 1000)}s: ${key}`,
      {
        level: "warning",
        tags: {
          component: "powersync_upload",
          app_version: appVersion(),
          pg_code: pgCode ?? "unknown",
          http_status:
            httpStatus === undefined ? "unknown" : String(httpStatus),
          failure_policy: "stalled_queue_head",
          transient_kind: transientKind,
        },
        contexts: {
          powersync_upload: {
            operation_count: transaction.crud.length,
            operation_summary: transactionOperationSummary(transaction),
            has_observation_delete: hasObservationDelete(transaction),
            stalled_ms: stalledMs,
          },
          last_observation_delete: getLastObservationDeleteDiagnosticContext(),
        },
      },
    )

    setQueueHeadStallRecord({ ...existing, reported: true })
  } catch {
    // Telemetry must never interfere with queue recovery.
  }
}

export class SupabaseConnector implements PowerSyncBackendConnector {
  async fetchCredentials() {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession()

    if (!session || error) {
      throw new Error("Not authenticated — cannot connect to PowerSync")
    }

    return {
      endpoint: POWERSYNC_URL,
      token: session.access_token,
    }
  }

  async uploadData(database: CommonPowerSyncDatabase): Promise<void> {
    const iterator = database.getCrudTransactions()[Symbol.asyncIterator]()
    const head = await readForUpload(iterator)
    if (!head) {
      await markUploadQueueEmpty()
      return
    }
    // A token check from an earlier call says nothing about this one.
    tokenCheck = null

    // No connectivity gate here: uploadData runs inside PowerSync's CRUD
    // lock, and disconnect() waits for it, so waiting for an "online" event
    // would hang logout while offline. navigator.onLine is also unreliable
    // in WebViews. Offline, the requests below fail as transient errors and
    // PowerSync retries after its own delay. Anything awaited here still
    // delays disconnect(), so it must be bounded: the live-session lookup
    // gives up after 30s.

    // Do not start a row upload with a stale cached/offline session. If the
    // live session is unavailable, leave the CRUD entry queued so PowerSync
    // can retry it after auth recovers.
    const session = await getLiveSessionWithTimeout()
    if (!session) {
      reportRowsWaitingForConnection()
      throw new Error("No live Supabase session — leaving upload queued")
    }

    // Send every request in this upload with exactly this token. The
    // shared client resolves its token per request and can fall back to
    // `anon` if a refresh stalls, which would leave a 42501 below impossible
    // to attribute. Binding the token lets us verify the one that was used.
    const client = getTokenBoundClient(session.access_token)

    // A rejected batch is narrowed down by halving, straight away, until a
    // part of it uploads or it is down to the head alone (see
    // RejectionWindow).
    let batch = await collectBatch(head, iterator)
    while (batch.length > 1) {
      const uploaded = await uploadBatch(
        database,
        client,
        session.access_token,
        batch,
      )
      if (uploaded) return
      batch = narrowAfterRejection(batch)
    }

    await uploadTransaction(database, client, session.access_token, head)
  }
}

/**
 * The auth server's verdict on the token of the current uploadData call,
 * asked at most once per call. Narrowing a rejected batch down can meet
 * several denials in one call, all sent with the same token, so there's no
 * need to ask again for each (each check is a network round trip).
 */
let tokenCheck: { accessToken: string; accepted: Promise<boolean> } | null =
  null

function isTokenConfirmed(accessToken: string): Promise<boolean> {
  if (tokenCheck?.accessToken !== accessToken) {
    tokenCheck = { accessToken, accepted: isAccessTokenAccepted(accessToken) }
  }
  return tokenCheck.accepted
}

/**
 * Rethrow an upload failure that isn't about the rows being sent, so they
 * stay queued exactly as they are for PowerSync to retry. Returns normally
 * when the server rejected the rows themselves, and the caller decides what
 * to do about that.
 *
 * `label` names what was being uploaded, for the log line; `key` and
 * `transaction` identify the queue head for the stall signal.
 */
async function leaveQueuedUnlessRowsRejected({
  error,
  accessToken,
  key,
  transaction,
  label,
}: {
  error: unknown
  accessToken: string
  key: string
  transaction: CrudTransaction
  label: string
}): Promise<void> {
  const pgCode = errorField(error, "code")

  // 42501 (insufficient_privilege) is ambiguous: it is also what an
  // expired-token request gets back from RLS. Ask the auth server about
  // the exact token the request carried. If it can't be confirmed, the
  // problem is auth, not the rows — every later upload would fail the same
  // way — so throw to keep them queued for PowerSync to retry. Otherwise
  // it's a genuine denial for a row, and so a rejection. A PATCH matching
  // zero rows gets the same check: RLS filtering the row out is a denial
  // too, and a revoked session can cause it.
  const deniedForRow = pgCode === "42501" || pgCode === ROW_NOT_FOUND_CODE
  if (deniedForRow && !(await isTokenConfirmed(accessToken))) {
    console.warn(
      `[PowerSync] RLS error for ${label} with an unconfirmed token, leaving queued:`,
      error,
    )
    recordQueueHeadStall(key, error, transaction, "unconfirmed_token")
    // From the user's side this is a connection problem, not a stuck item.
    reportRowsWaitingForConnection()
    throw error
  }

  // Permanent codes are about the row, whatever HTTP status came with them.
  if (pgCode && PERMANENT_ERROR_CODES.has(pgCode)) return

  // Known-transient failures (network/fetch failures, 5xx, 408/429/401,
  // JWT/connection/resource-exhaustion codes, or a raw thrown exception
  // that never reached PostgREST at all) — rethrow untouched by the
  // retry budget so PowerSync retries the upload as-is. These never count
  // toward MAX_NON_TRANSIENT_RETRIES and never land in sync_failures,
  // since none of them say a row itself is bad.
  const transientKind = transientErrorKind(error)
  if (transientKind) {
    console.error(
      `[PowerSync] Transient error (${transientKind}) uploading ${label}, leaving queued:`,
      error,
    )
    recordQueueHeadStall(key, error, transaction, transientKind)
    if (transientKind === "network") {
      reportRowsWaitingForConnection()
    } else {
      reportRowQueueHeadFailing()
    }
    throw error
  }
}

/**
 * Upload several transactions together, completing them all at once.
 *
 * Returns false when the server rejected something in the batch. A batch
 * error can't say which row it was, so the caller narrows it down by
 * halving (see RejectionWindow).
 *
 * Errors that aren't about the rows (network, server, an unconfirmed
 * token) are rethrown as-is, the same as for a single transaction, and the
 * next attempt batches again.
 */
async function uploadBatch(
  database: CommonPowerSyncDatabase,
  client: TokenBoundClient,
  accessToken: string,
  batch: CrudTransaction[],
): Promise<boolean> {
  const head = batch[0]
  const last = batch[batch.length - 1]
  const headKey = transactionKey(head)

  try {
    await uploadOperations(
      client,
      database,
      batch.flatMap((transaction) => transaction.crud),
    )
  } catch (error: unknown) {
    await leaveQueuedUnlessRowsRejected({
      error,
      accessToken,
      key: headKey,
      transaction: head,
      label: `batch starting ${headKey}`,
    })

    console.warn(
      `[PowerSync] Batch of ${batch.length} transactions starting ${headKey} was rejected, narrowing it down:`,
      error,
    )
    return false
  }

  for (const transaction of batch) {
    clearRetryCount(transactionKey(transaction))
  }
  clearQueueHeadStall()
  // Completing the last transaction completes every one before it.
  await last.complete()
  reportRowsUploaded()
  recordPowerSyncUploadAfterObservationDelete({
    operationCount: batch.reduce(
      (count, transaction) => count + transaction.crud.length,
      0,
    ),
    operationSummary: batchOperationSummary(batch),
  })
  return true
}

/**
 * Upload one transaction, classifying any failure: park it in
 * sync_failures, retry it with backoff, or leave it queued and rethrow.
 */
async function uploadTransaction(
  database: CommonPowerSyncDatabase,
  client: TokenBoundClient,
  accessToken: string,
  transaction: CrudTransaction,
): Promise<void> {
  const key = transactionKey(transaction)

  try {
    await uploadOperations(client, database, transaction.crud)

    // Success — clear retry tracking and complete
    clearRetryCount(key)
    clearQueueHeadStall()
    await transaction.complete()
    reportRowsUploaded()
    captureObservationDeleteUploaded(transaction)
    recordPowerSyncUploadAfterObservationDelete({
      operationCount: transaction.crud.length,
      operationSummary: transactionOperationSummary(transaction),
    })
  } catch (error: unknown) {
    await leaveQueuedUnlessRowsRejected({
      error,
      accessToken,
      key,
      transaction,
      label: key,
    })

    const pgCode = errorField(error, "code")

    // Permanently non-retryable errors (including a 42501 the auth server
    // confirmed was sent with a valid token) — save data and discard,
    // rather than hold up the rest of the queue
    if (pgCode && PERMANENT_ERROR_CODES.has(pgCode)) {
      console.error(
        `[PowerSync] Permanent error uploading ${key}, saving to sync_failures:`,
        error,
      )
      captureUploadFailure(error, transaction, "permanent")
      await saveFailedTransaction(database, transaction, error)
      clearRetryCount(key)
      clearQueueHeadStall()
      await safeComplete(transaction)
      return
    }

    // A row whose parent's create is parked can never upload, so retrying it
    // would only hold up the queue for a minute or so. Park it with the
    // parent instead. A parent that is merely late isn't parked, so its
    // children still get the retries below.
    const parkedParent = isMissingParentError(error)
      ? await findParkedParent(database, transaction)
      : null
    if (parkedParent) {
      await parkWithParkedParent({
        database,
        transaction,
        error,
        key,
        parent: parkedParent,
      })
      return
    }

    // Dependency errors (FK violations) — retry with backoff, then discard
    if (pgCode && DEPENDENCY_ERROR_CODES.has(pgCode)) {
      await retryWithBackoffOrDiscard({
        database,
        transaction,
        error,
        key,
        failurePolicy: "dependency_exhausted",
        logLabel: "Dependency error",
      })
      return
    }

    // Everything else: the server rejected this row outright (schema
    // drift like PGRST204, invalid input, a non-species unique
    // violation, a 4xx with no recognizable code, ...). Retry a few
    // times in case it clears on its own, then park it — since
    // PowerSync's CRUD queue is strictly FIFO, leaving a truly poison
    // op queued forever would block every later transaction too.
    await retryWithBackoffOrDiscard({
      database,
      transaction,
      error,
      key,
      failurePolicy: "rejected_exhausted",
      logLabel: "Rejected by server",
    })
  }
}
